/**
 * API service for Board Template operations
 * Handles CRUD operations for board templates
 */

/**
 * Fetch all board templates accessible to the current user
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {Object} params - Query parameters (category, includeInactive)
 * @returns {Promise<Array>} Array of board templates
 */
export const fetchBoardTemplates = async (apiFetch, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/api/BoardTemplates?${queryString}` : '/api/BoardTemplates';
    const response = await apiFetch(url);
    const data = await response.json();
    if (data.success) {
        return data.data || [];
    }
    throw new Error(data.message || 'Failed to fetch board templates');
};

/**
 * Fetch templates grouped by category
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @returns {Promise<Array>} Array of grouped templates
 */
export const fetchBoardTemplatesByCategory = async (apiFetch) => {
    const response = await apiFetch('/api/BoardTemplates/by-category');
    const data = await response.json();
    if (data.success) {
        return data.data || [];
    }
    throw new Error(data.message || 'Failed to fetch templates by category');
};

/**
 * Fetch details of a specific board template including columns
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} templateId - The ID of the template to fetch
 * @returns {Promise<Object>} Template details
 */
export const fetchBoardTemplate = async (apiFetch, templateId) => {
    const response = await apiFetch(`/api/BoardTemplates/${templateId}`);
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to fetch template details');
};

/**
 * Create a new board template
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {Object} templateData - Template creation data
 * @returns {Promise<Object>} Created template data
 */
export const createBoardTemplate = async (apiFetch, templateData) => {
    const response = await apiFetch('/api/BoardTemplates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
    });
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to create template');
};

/**
 * Update an existing board template
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} templateId - The ID of the template to update
 * @param {Object} updates - Updated template data
 * @returns {Promise<Object>} Update response
 */
export const updateBoardTemplate = async (apiFetch, templateId, updates) => {
    const response = await apiFetch(`/api/BoardTemplates/${templateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to update template');
};

/**
 * Delete a board template
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} templateId - The ID of the template to delete
 * @returns {Promise<Object>} Delete response
 */
export const deleteBoardTemplate = async (apiFetch, templateId) => {
    const response = await apiFetch(`/api/BoardTemplates/${templateId}`, {
        method: 'DELETE'
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to delete template');
};

/**
 * Reactivate a deactivated system template
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} templateId - The ID of the template to reactivate
 * @returns {Promise<Object>} Reactivation response
 */
export const reactivateBoardTemplate = async (apiFetch, templateId) => {
    const response = await apiFetch(`/api/BoardTemplates/${templateId}/reactivate`, {
        method: 'PATCH'
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to reactivate template');
};
